import { createInstance } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';

import esPY from '@mc/i18n/es-PY.json' with { type: 'json' };

/** Idiomas de la interfaz (docs/10, ADR-041). En la base el locale es `es_PY`; en i18next, `es-PY`. */
export const LANGUAGES = ['es-PY', 'en-US', 'pt-BR'] as const;
export type Language = (typeof LANGUAGES)[number];
const STORAGE_KEY = 'mc.lang';

const isLanguage = (value: string | null | undefined): value is Language =>
  (LANGUAGES as readonly string[]).includes(value ?? '');

export const toLanguage = (dbLocale: string | null | undefined): Language => {
  const language = dbLocale?.replace('_', '-');
  return isLanguage(language) ? language : 'es-PY';
};
export const toDbLocale = (language: string): 'es_PY' | 'en_US' | 'pt_BR' =>
  isLanguage(language) ? (language.replace('-', '_') as 'es_PY' | 'en_US' | 'pt_BR') : 'es_PY';

function initialLanguage(): Language {
  const saved = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);
  return isLanguage(saved) ? saved : 'es-PY';
}

/**
 * es-PY va en el bundle (idioma de la clínica y de respaldo); los otros se bajan al elegirlos:
 * cada diccionario son ~38 KB gzip (RNF-PERF-05).
 */
const LOADERS: Record<Exclude<Language, 'es-PY'>, () => Promise<{ default: object }>> = {
  'en-US': () => import('@mc/i18n/en-US.json'),
  'pt-BR': () => import('@mc/i18n/pt-BR.json'),
};

/** Instancia propia (no la global de i18next): la app y los tests usan la misma. */
const i18n = createInstance();

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources: { 'es-PY': { translation: esPY } },
    lng: 'es-PY',
    fallbackLng: 'es-PY',
    interpolation: { escapeValue: false },
    returnNull: false,
  });

async function load(language: Language): Promise<void> {
  if (language === 'es-PY' || i18n.hasResourceBundle(language, 'translation')) return;
  const { default: dictionary } = await LOADERS[language]();
  i18n.addResourceBundle(language, 'translation', dictionary);
}

/** Cambia el idioma sin recargar (RNF-UX-05): los formularios conservan lo escrito. */
export async function setLanguage(language: Language): Promise<void> {
  localStorage.setItem(STORAGE_KEY, language);
  await load(language);
  document.documentElement.lang = language;
  await i18n.changeLanguage(language);
}

/** El idioma guardado, ya cargado: `main.tsx` espera esto antes de pintar (sin parpadeo en español). */
export const i18nReady: Promise<void> = (async () => {
  const language = initialLanguage();
  await load(language).catch(() => undefined);
  document.documentElement.lang = i18n.hasResourceBundle(language, 'translation') ? language : 'es-PY';
  await i18n.changeLanguage(document.documentElement.lang);
})();

export default i18n;
